import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface FaqItem {
    question: string;
    answer: string;
}

const ContactFaq: React.FC = () => {
    const { t } = useTranslation();
    const [openItems, setOpenItems] = useState<Set<number>>(new Set());

    const faqItems = t('faq.items', { returnObjects: true }) as FaqItem[];

    const toggleItem = useCallback((index: number) => {
        setOpenItems(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    }, []);

    // Split items into two columns
    const midpoint = Math.ceil(faqItems.length / 2);
    const leftItems = faqItems.slice(0, midpoint);
    const rightItems = faqItems.slice(midpoint);

    const scrollToContact = () => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const renderFaqItem = (item: FaqItem, globalIndex: number) => {
        const isOpen = openItems.has(globalIndex);
        return (
            <div
                key={globalIndex}
                className="faq-accordion"
                onClick={() => toggleItem(globalIndex)}
            >
                <div className="faq-questio-wrapper">
                    <h5 className="heading-style-h5">{item.question}</h5>
                    <div
                        className="faq-icon-wrapper"
                        style={{
                            transform: isOpen
                                ? 'translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(45deg) skew(0deg, 0deg)'
                                : 'translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)',
                            transformStyle: 'preserve-3d',
                            transition: 'transform 0.3s ease',
                        }}
                    >
                        <div className="faq-icon horizontal"></div>
                        <div className="faq-icon vertical"></div>
                    </div>
                </div>
                <div
                    className="faq-answer-wrapper"
                    style={{
                        height: isOpen ? 'auto' : '0px',
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen
                            ? 'translate3d(0px, 0px, 0px) scale3d(1, 1, 1)'
                            : 'translate3d(0px, 20px, 0px) scale3d(0.96, 0.96, 1)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        transformStyle: 'preserve-3d',
                    }}
                >
                    <div className="margin-buttom margin-top-xxsmall">
                        <p className="text-size-regular" style={{ whiteSpace: 'pre-line' }}>
                            {item.answer}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="section-faq">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-section-large">
                        <div className="faq-component">
                            <div className="faq-top-content-wrapper">
                                <div className="heading-wrapper">
                                    <h2 className="heading-style-h2">{t('faq.title1')}</h2>
                                    <div className="line-color-pink" style={{ width: '100%', height: '15px' }}></div>
                                </div>
                                {t('faq.title2') && (
                                    <div className="heading-wrapper">
                                        <h2 className="heading-style-h2">{t('faq.title2')}</h2>
                                        <div className="line-color-light-golden" style={{ height: '14px' }}></div>
                                    </div>
                                )}
                                <div className="padding-bottom padding-small"></div>
                                <p className="text-size-regular">{t('faq.subtitle')}</p>
                                <div className="padding-bottom padding-small"></div>
                                <button
                                    className="button w-button"
                                    onClick={scrollToContact}
                                >
                                    {t('faq.contactButton')}
                                </button>
                            </div>
                            <div className="padding-bottom padding-xlarge"></div>
                            <div className="w-layout-grid faq-grid">
                                <div className="faq-list">
                                    {leftItems.map((item, index) =>
                                        renderFaqItem(item, index)
                                    )}
                                </div>
                                <div className="faq-list">
                                    {rightItems.map((item, index) =>
                                        renderFaqItem(item, index + midpoint)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactFaq;
